import { saveAs } from 'file-saver';
import hashIt from 'hash-it';
import html2canvas from 'html2canvas';
import isNumber from 'lodash/isNumber';
import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { SelectedRepetitionChangeStream } from '../../../data/change_streams/selected_repetition/selected_repetition_change_stream';
import {
  IGraph,
  ISize,
  ITest,
  ITestRepetition,
  ITimingEvent,
} from '../../../data/types';
import { excludeRepetitions } from '../../../data/writers/repetitions';
import {
  Repetition,
  RepetitionsEditState,
  useEditRepetitions,
  UseEditRepetitionsReturn,
} from '../../../hooks/repetitions/useEditRepetitions';
import { useGraphData } from '../../../hooks/useGraphData';
import { roundValue } from '../../../utils/math';
import { Column } from '../../layout/Column';
import { GraphContainer } from '../../shared/graphs/GraphContainer';
import { Card } from '../card/Card';
import { CardHeader } from '../card/CardHeader';
import { LinearLoading } from '../loading/LinearLoading';
import { EPdfMode, isPdf, shouldInvert } from '../pdf/Pdf';
import { EditRepetitions } from './force_time/EditRepetitions';
import { GraphMenu } from './GraphMenu';
import { legendFormatter } from './legendFormatter';
import { PhotoExportMenu } from './PhotoExportMenu';
import { CustomTooltip } from './Tooltip';

interface IProps {
  test: ITest;
  style?: React.CSSProperties;
  pdfMode?: EPdfMode;
  repetitions?: ITestRepetition[];
  selectedRepetition?: ITestRepetition;
  selectedRepetitionChangeStream?: SelectedRepetitionChangeStream;
  repetitionsLoading?: boolean;
  graphData?: IGraphData[];
  graphRaw?: IGraph;
  timingEvents?: ITimingEvent[];
  dataMin?: number;
  isLoading?: boolean;
  shouldFetchData?: boolean;
  editRepetitions?: UseEditRepetitionsReturn;
}

interface IGraphData {
  Combined: number;
  Left: number;
  Right: number;
  XLeft: number;
  XRight: number;
  YLeft: number;
  YRight: number;
  Velocity: number;
  Displacement: number;
  Power: number;
  Time: number;
  Hertz: number;
}

const ForceTime = (props: IProps): React.ReactElement => {
  const [showForce, setShowForce] = React.useState<boolean>(true);
  const [showVelocity, setShowVelocity] = React.useState<boolean>(false);
  const [showDisplacement, setShowDisplacement] =
    React.useState<boolean>(false);
  const [showPower, setShowPower] = React.useState<boolean>(false);
  const [show3d, setShow3d] = React.useState<boolean>(false);

  // Use the explicit prop, defaulting to true if not provided
  const shouldFetch = props.shouldFetchData !== false;

  const {
    graphData: fetchedGraphData,
    graphRaw: fetchedGraphRaw,
    timingEvents: fetchedTimingEvents,
    dataMin: fetchedDataMin,
    loading: fetchLoading,
  } = useGraphData(props.test, shouldFetch);

  // Use either the props data or the fetched data
  const graphData = props.graphData || fetchedGraphData;
  const graphRaw = props.graphRaw || fetchedGraphRaw;
  const timingEvents = props.timingEvents || fetchedTimingEvents;
  const dataMin = props.dataMin !== undefined ? props.dataMin : fetchedDataMin;
  const loading =
    props.isLoading !== undefined ? props.isLoading : fetchLoading;

  const isDualPlate = React.useMemo((): boolean => {
    return isNumber(graphData?.[0]?.Left);
  }, [hashIt(graphData)]);

  // Calculate the maximum Y value for force data
  const getForceYMax = React.useMemo((): number => {
    if (!graphData || graphData.length === 0) return 0;

    // Find the maximum value across all force metrics
    return Math.max(
      ...graphData.map((point: IGraphData): number => {
        const values = [];
        if (isDualPlate) {
          values.push(point.Left || 0, point.Right || 0);
        }
        values.push(point.Combined || 0);
        if (show3d) {
          values.push(
            point.XLeft || 0,
            point.XRight || 0,
            point.YLeft || 0,
            point.YRight || 0
          );
        }
        return Math.max(...values);
      }),
      graphRaw?.target || 0
    );
  }, [hashIt(graphData), isDualPlate, show3d, graphRaw?.target]);

  const getSystemWeight = React.useCallback((): number => {
    const { testType, weight }: Partial<ITest> = props.test ? props.test : {};
    if (testType.repetitions === true) {
      if (graphRaw?.systemWeight) {
        return graphRaw.systemWeight;
      }
    } else {
      return typeof weight === 'object' ? weight.value : weight;
    }
  }, [hashIt(props.test), hashIt(graphRaw)]);

  /**
   * @description export the photo
   * @param backgroundColor
   */
  const onExportPhoto = (backgroundColor?: string): void => {
    const { test } = props;
    html2canvas(document.querySelector('.export-container'), {
      backgroundColor,
    }).then((canvas: HTMLCanvasElement): void => {
      setTimeout((): void => {
        saveAs(
          canvas.toDataURL(),
          `${test.athlete.name}-${test.testType.name}.png`
        );
      }, 1000);
    });
  };

  /**
   * @description whether to show the timing events
   * @returns boolean
   */
  const showTimingEvents = React.useMemo((): boolean => {
    const isRepetitionTest = props?.test?.testType?.repetitions === true;
    const hasRepetitions = props?.repetitions?.length > 0;
    const isLoadingRepetitions = props.repetitionsLoading === true;

    // Only show timing events when we're loading repetitions
    // but not when we have a repetition test with repetitions
    if (isRepetitionTest && (hasRepetitions || isLoadingRepetitions)) {
      return false;
    }

    return true;
  }, [
    props?.test?.testType?.repetitions,
    props?.repetitions?.length,
    props.repetitionsLoading,
  ]);

  /// if the editRepetitions prop is not provided, initialize the editRepetitions hook here
  const editRepetitions =
    props.editRepetitions ||
    useEditRepetitions({
      enabledRepetitions: props.repetitions,
      disabledRepetitions: graphRaw.excludedWindows?.map(
        (window: { start: number; end: number }): Repetition => ({
          start: window.start,
          end: window.end,
        })
      ),
      onSave: async (disabled: Repetition[]): Promise<void> => {
        await excludeRepetitions({
          testId: props.test.id,
          repetitions: disabled,
        });
      },
    });

  /**
   * @description disabled repetitions
   * @returns Repetition[]
   */
  const disabledRepetitions = React.useMemo((): Repetition[] => {
    return (
      graphRaw.excludedWindows?.map(
        (window: { start: number; end: number }): Repetition => ({
          start: window.start,
          end: window.end,
        })
      ) ?? []
    );
  }, [hashIt(graphRaw.excludedWindows)]);

  /**
   * @description render the repetition
   * @param rep - the repetition to render
   * @param isEditing - whether the repetitions are being edited
   * @param isActive - whether the repetition is active
   * @returns React.ReactElement
   */
  const renderRepetition = (
    rep: ITestRepetition,
    isEditing: boolean = false,
    isActive: boolean = false
  ): React.ReactElement => {
    const dotRadius = 10;

    let refAreaFillOpacity = 0.2;

    switch (hashIt([isEditing, isActive])) {
      case hashIt([true, false]):
        refAreaFillOpacity = 0.1;
        break;
      case hashIt([true, true]):
        refAreaFillOpacity = 0.2;
        break;
      default:
        refAreaFillOpacity = 0.2;
        break;
    }

    return (
      <React.Fragment key={`rep-${rep.id}`}>
        <ReferenceArea
          yAxisId='left'
          x1={rep.start}
          x2={rep.end}
          ifOverflow='extendDomain'
          stroke='var(--color-material-500-blue-gray)'
          fill='var(--color-material-500-blue-gray)'
          fillOpacity={refAreaFillOpacity}
          strokeOpacity={0}
        />
        {isEditing && isActive && (
          <ReferenceDot
            yAxisId='left'
            x={rep.start}
            y={getForceYMax}
            fill='red'
            stroke='red'
            shape={(props) => {
              const { cx, cy } = props;
              return (
                <g
                  style={{ cursor: 'pointer' }}
                  onClick={(): void => {
                    editRepetitions.addToDisabled({
                      start: rep.start,
                      end: rep.end,
                    });
                  }}
                >
                  <circle
                    cx={cx + dotRadius}
                    cy={cy + dotRadius}
                    r={dotRadius}
                    fill='green'
                  />
                  <text
                    x={cx + dotRadius}
                    y={cy + dotRadius}
                    textAnchor='middle'
                    dominantBaseline='middle'
                    fill='white'
                    fontSize='10px'
                  >
                    &#x2713;
                  </text>
                </g>
              );
            }}
          />
        )}
        {isEditing && !isActive && (
          <ReferenceDot
            yAxisId='left'
            x={rep.start}
            y={getForceYMax}
            r={10}
            shape={(props) => {
              const { cx, cy } = props;
              return (
                <g
                  style={{ cursor: 'pointer' }}
                  onClick={(): void => {
                    editRepetitions.addToEnabled({
                      start: rep.start,
                      end: rep.end,
                    });
                  }}
                >
                  <circle
                    cx={cx + dotRadius}
                    cy={cy + dotRadius}
                    r={dotRadius}
                    fill='red'
                  />
                  <text
                    x={cx + dotRadius}
                    y={cy + dotRadius}
                    textAnchor='middle'
                    dominantBaseline='middle'
                    fill='white'
                    fontSize='10px'
                  >
                    &#x78;
                  </text>
                </g>
              );
            }}
          />
        )}
      </React.Fragment>
    );
  };

  const renderRepetitionsSection = React.useMemo((): React.ReactElement[] => {
    let repsToRender = [];

    // When editing or no selection exists, show all repetitions
    if (
      props.selectedRepetition === null &&
      editRepetitions.editState === RepetitionsEditState.Editing
    ) {
      repsToRender = [...props.repetitions, ...disabledRepetitions];
    } else if (props.selectedRepetition !== null) {
      repsToRender = [props.selectedRepetition];
    } else {
      repsToRender = props.repetitions;
    }

    return repsToRender.map(
      (rep: ITestRepetition): React.ReactElement =>
        renderRepetition(
          rep,
          editRepetitions.editState === RepetitionsEditState.Editing,
          editRepetitions.isEnabled({
            start: rep.start,
            end: rep.end,
          })
        )
    );
  }, [
    hashIt(disabledRepetitions),
    hashIt(props.selectedRepetition),
    hashIt(props.repetitions),
    editRepetitions.editState,
    editRepetitions.stateHash,
  ]);

  return (
    <ForceTimeCard
      style={{
        ...props.style,
        flex: 3,
      }}
    >
      <Column>
        <CardHeader>
          <HeaderLeft>
            <EditRepetitions
              repetitions={props.repetitions}
              selectedRepetition={props.selectedRepetition}
              selectedRepetitionChangeStream={
                props.selectedRepetitionChangeStream
              }
              editRepetitions={editRepetitions}
            />
            <GraphMenu
              is3d={graphRaw?.xForceRight?.length > 0}
              velocityAvailable={graphRaw?.velocity?.length > 0}
              forceSelected={showForce}
              onForceSelect={setShowForce}
              threeDSelected={show3d}
              on3dSelected={setShow3d}
              powerSelected={showPower}
              onPowerSelected={setShowPower}
              velocitySelected={showVelocity}
              onVelocitySelected={setShowVelocity}
              displacementSelected={showDisplacement}
              onDisplacementSelected={setShowDisplacement}
            />
          </HeaderLeft>

          <HeaderRight>
            {!isPdf(props.pdfMode) && (
              <PhotoExportMenu onExportPhoto={onExportPhoto} />
            )}
          </HeaderRight>
        </CardHeader>

        {!loading ? (
          <GraphContainer inverse={shouldInvert(props.pdfMode)}>
            <AutoSizer>
              {(size: ISize): React.ReactElement => {
                return (
                  <LineChart
                    height={size.height}
                    width={size.width}
                    data={graphData}
                    margin={{ top: 0, right: 10, left: 10, bottom: 10 }}
                    className='export-container'
                  >
                    <CartesianGrid
                      stroke='rgba(255,255,255,.2)'
                      strokeWidth={0.25}
                    />
                    {showTimingEvents &&
                      timingEvents?.map(
                        (evt: ITimingEvent, i: number): React.ReactElement => {
                          return (
                            <ReferenceArea
                              key={i}
                              yAxisId='left'
                              x1={evt.start}
                              x2={evt.end}
                              ifOverflow='extendDomain'
                              stroke={evt.color}
                              fill={evt.color}
                              fillOpacity={0.2}
                              strokeOpacity={0}
                            />
                          );
                        }
                      )}
                    {getSystemWeight() && showForce && (
                      <ReferenceLine
                        yAxisId='left'
                        y={getSystemWeight()}
                        ifOverflow='extendDomain'
                        stroke='white'
                        strokeWidth={0.5}
                        strokeOpacity={0.5}
                        label={{
                          value:
                            graphRaw?.plateModel === 'HDTS'
                              ? 'Pretension'
                              : 'System Weight',
                          position: 'insideTopRight',
                          fill: 'white',
                          fillOpacity: 0.5,
                        }}
                      />
                    )}

                    {/* render the repetitions */}
                    {renderRepetitionsSection}

                    {/* render the target */}
                    {graphRaw?.target !== null &&
                      showForce &&
                      props?.test?.testType?.repetitions === true && (
                        <ReferenceLine
                          yAxisId='left'
                          y={graphRaw.target}
                          ifOverflow='extendDomain'
                          stroke='white'
                          strokeDasharray='3'
                          strokeWidth={1}
                          strokeOpacity={0.5}
                          label={{
                            value: 'Target',
                            position: 'insideTopRight',
                            fill: 'white',
                            fillOpacity: 0.5,
                          }}
                        />
                      )}

                    <XAxis
                      dataKey='Time'
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={timeTickFormatterFactory(graphRaw?.hertz)}
                      type='number'
                      domain={['dataMin', 'dataMax']}
                      tick={{ fill: 'var(--color-text' }}
                      tickCount={Math.floor(graphRaw?.hertz / 10)}
                      xAxisId={0}
                    />
                    {showVelocity && [
                      <YAxis
                        key='v-0'
                        dataKey='Velocity'
                        domain={['dataMin', 'dataMax']}
                        yAxisId='right'
                        orientation='right'
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--color-text' }}
                        tickFormatter={velocityTickFormatter}
                        tickCount={100}
                        type='number'
                      >
                        <Label
                          position='insideRight'
                          offset={15}
                          dy={35}
                          angle={90}
                          value='Velocity (m/s)'
                          style={{ fill: 'var(--color-text)' }}
                        />
                      </YAxis>,
                      <Line
                        key='v-1'
                        dataKey='Velocity'
                        dot={false}
                        isAnimationActive={false}
                        yAxisId='right'
                        stroke='var(--color-material-500-light-green)'
                        strokeWidth={2.5}
                      />,
                    ]}
                    {showDisplacement && [
                      <Line
                        key='d-0'
                        dataKey='Displacement'
                        dot={false}
                        isAnimationActive={false}
                        yAxisId='right'
                        stroke='var(--color-material-500-light-green)'
                        strokeWidth={2.5}
                      />,
                      <YAxis
                        key='d-1'
                        dataKey='Displacement'
                        domain={['dataMin', 'dataMax']}
                        yAxisId='right'
                        orientation='right'
                        tickFormatter={displacementTickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--color-text' }}
                        tickCount={100}
                        type='number'
                      >
                        <Label
                          position='insideRight'
                          offset={0}
                          dy={50}
                          angle={90}
                          value='Displacement (m)'
                          style={{ fill: 'var(--color-text)' }}
                        />
                      </YAxis>,
                    ]}
                    {showPower && [
                      <Line
                        key='p-0'
                        dataKey='Power'
                        dot={false}
                        isAnimationActive={false}
                        yAxisId='right'
                        stroke='var(--color-material-500-light-green)'
                        strokeWidth={2.5}
                      />,
                      <YAxis
                        key='p-1'
                        dataKey='Power'
                        tickFormatter={powerTickFormatter}
                        domain={['dataMin', 'dataMax']}
                        yAxisId='right'
                        orientation='right'
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'var(--color-text' }}
                        tickCount={100}
                        type='number'
                      >
                        <Label
                          position='insideRight'
                          offset={0}
                          dy={25}
                          angle={90}
                          value='Power (W)'
                          style={{ fill: 'var(--color-text)' }}
                        />
                      </YAxis>,
                    ]}
                    <YAxis
                      key='f-0'
                      dataKey='Combined'
                      domain={[
                        show3d ? dataMin : 0,
                        (dataMax: number): number => dataMax,
                      ]}
                      yAxisId='left'
                      axisLine={false}
                      tickLine={false}
                      allowDataOverflow
                      tick={{ fill: 'var(--color-text)' }}
                      type='number'
                      tickCount={100}
                      tickFormatter={(tick: string): string =>
                        `${roundValue(+tick, 0)}`
                      }
                    >
                      <Label
                        position='insideLeft'
                        offset={5}
                        angle={-90}
                        value='Newtons'
                        style={{ fill: 'var(--color-text)' }}
                      />
                    </YAxis>

                    {showForce && [
                      isDualPlate ? (
                        <Line
                          key='f-2'
                          dataKey='Right'
                          dot={false}
                          isAnimationActive={false}
                          yAxisId='left'
                          stroke='var(--color-material-500-orange)'
                          strokeWidth={2.5}
                        />
                      ) : null,
                      isDualPlate ? (
                        <Line
                          key='f-3'
                          dataKey='Left'
                          dot={false}
                          isAnimationActive={false}
                          yAxisId='left'
                          stroke='var(--color-material-500-cyan)'
                          strokeWidth={2.5}
                        />
                      ) : null,
                      <Line
                        key='f-1'
                        dataKey='Combined'
                        dot={false}
                        isAnimationActive={false}
                        yAxisId='left'
                        stroke='var(--color-material-500-blue)'
                        strokeWidth={2.5}
                      />,
                    ]}

                    {show3d && [
                      isDualPlate ? (
                        <Line
                          key='f-3'
                          dataKey='XLeft'
                          dot={false}
                          isAnimationActive={false}
                          yAxisId='left'
                          stroke='var(--color-material-500-blue-gray)'
                          strokeWidth={2.5}
                        />
                      ) : null,
                      <Line
                        key='f-4'
                        dataKey='XRight'
                        dot={false}
                        isAnimationActive={false}
                        yAxisId='left'
                        stroke='var(--color-material-500-pink)'
                        strokeWidth={2.5}
                      />,
                      isDualPlate ? (
                        <Line
                          key='f-5'
                          dataKey='YLeft'
                          dot={false}
                          isAnimationActive={false}
                          yAxisId='left'
                          stroke='var(--color-material-500-amber)'
                          strokeWidth={2.5}
                        />
                      ) : null,
                      <Line
                        key='f-6'
                        dataKey='YRight'
                        dot={false}
                        isAnimationActive={false}
                        yAxisId='left'
                        stroke='skyblue'
                        strokeWidth={2.5}
                      />,
                    ]}

                    <Legend
                      iconType='circle'
                      formatter={(value) =>
                        legendFormatter(value, props.pdfMode)
                      }
                    />
                    <Tooltip content={CustomTooltip} />
                  </LineChart>
                );
              }}
            </AutoSizer>
          </GraphContainer>
        ) : (
          <LinearLoading />
        )}
      </Column>
    </ForceTimeCard>
  );
};

export { ForceTime };

const timeTickFormatterFactory = (hertz: number): ((t: string) => string) => {
  return (tick: string): string => `${roundValue(+tick / hertz, 2)}s`;
};

export const velocityTickFormatter = (tick: string): string =>
  `${roundValue(+tick, 1)}`;

export const powerTickFormatter = (tick: string): string =>
  `${roundValue(+tick, 0)}`;

export const displacementTickFormatter = (tick: string): string =>
  `${roundValue(+tick, 2)}`;

const HeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const HeaderRight = styled.div`
  flex: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ForceTimeCard = styled(Card as any)`
  padding: 0px;
  margin-top: var(--grid-gap);
  min-height: var(--graph-min-height);
`;
